import {
  ALLERGEN,
  ORDER_ORIGIN,
  ORDER_STATUS,
  SERVICE_TYPE as BACKEND_SERVICE_TYPE,
  VARIATION_TYPE,
} from '~../__queries__/globalTypes';
import {
  CompleteBongQuery_venue_company_categories_items_courseType,
  CompleteBongQuery_venue_company_items_upsales_items,
} from '~Scenes/POS/Queries/__queries__';
import { getUser_user } from '~__queries__';

export type Optional<T, K extends keyof T> = Pick<Partial<T>, K> & Omit<T, K>;

/**
 * THE TYPES IN THIS FILE ARE NOT MAPPED TO BACKEND MODELS
 * They are local versions of the backend data, adjusted to better
 * fit the needs of the OM app. Think twice before making these more
 * complex since they affect large portions of the code base
 */
export interface LocalVariation {
  id: string;
  name: string;
  price: number;
  asItem?: {
    id: string;
    entryId?: string;
  };
  allergen?: ALLERGEN;
  isOutOfStock?: boolean;
}

export interface LocalUpsell {
  id: string;
  name: TranslatedString;
  hideInEPOS: boolean;
  enableMultiSelect: boolean;
  items: CompleteBongQuery_venue_company_items_upsales_items[];
}

export interface LocalItem {
  id: string;
  itemId: string;
  categoryId: string;
  courseType?: CompleteBongQuery_venue_company_categories_items_courseType;
  entryId?: string;
  offerId?: string;
  variations: Array<LocalVariation>;
  removedVariations: Array<LocalVariation>;
  actionId?: number;
  title: string;
  description: string;
  type?: string;
  count?: number;
  message?: string | null;
  amount?: number;
  fraction?: number;
  instant?: boolean;
  discount?: number;
  discountTitle?: string;
  vatRate: number;
  takeAwayVatRate?: number | null;
  upsell?: LocalUpsell;
  upsales?: [LocalUpsell];
  imageUrl?: string;
  ageRestricted?: boolean;
  price?: number;
  isMessage?: boolean;
  parentId?: string;
  hasModificationGroups?: boolean;
  defaultOrderStatus?: ORDER_STATUS | null;
  modificationGroups?: string[];
  manualName?: boolean | null;
  manualPrice?: boolean | null;
  volumeSuffix?: string | null;
  allergyTags?: string[];
  costBasis?: number | null;
  disableMembershipDiscount?: boolean | null;
}

export interface CartItem {
  id: string;
  itemId: string;
  entryId: string;
  categoryId: string;
  courseType?: CompleteBongQuery_venue_company_categories_items_courseType;
  offerId?: string;
  variations: Array<LocalVariation>;
  removedVariations: Array<LocalVariation>;
  actionId?: number;
  title: string;
  description: string;
  type?: string;
  count: number;
  message?: string | null;
  amount: number;
  originalAmount?: number;
  fraction: number;
  instant?: boolean;
  discount: number;
  discountTitle?: string;
  vatRate: number;
  takeAwayVatRate?: number | null;
  imageUrl?: string;
  ageRestricted?: boolean;
  isMessage?: boolean;
  parentId?: string;
  defaultOrderStatus?: ORDER_STATUS | null;
  manualName?: boolean | null;
  disableMembershipDiscount?: boolean | null;
}

export interface LocalEntry {
  id: string;
  item: {
    id: string;
    title: string;
    vatRate: number;
    categoryId: string;
  };
  fraction?: number;
  instant?: boolean;
  price: number;
}

export type Languages = 'se' | 'en' | 'es' | 'no' | 'dk' | 'tr';
export const LANGUAGES: Languages[] = ['se', 'en', 'es', 'no', 'dk', 'tr'];

export type TranslatedString = {
  id: string;
  en: string;
  se: string;
  es?: string;
  no?: string;
};

export type useI18nProps = () => {
  i18n: (obj: TranslatedString | string | null) => string;
  language: Languages;
  changeLanguage: (lang: Languages) => void;
};

// Value is used to replace placeholders in the string or conditional translations
export type i18nProps = (obj: TranslatedString | null | string, value?: string | number) => string;

export type POSItemsId = {
  id: string;
  quantity: number;
  outOfStock: boolean;
};

export type POSItemsIdsState = POSItemsId[];

export interface Station {
  id: string;
  name: string;
  items: string[];
  categories: { id: string; name: string }[];
  orderStatuses: ORDER_STATUS[];
  origins: ORDER_ORIGIN[];
  serviceTypes: BACKEND_SERVICE_TYPE[];
  showAllItems: boolean;
}

export interface Zone {
  id: string;
  name: string;
  createdAt: string;
  deleted: Boolean;
  tables: {
    id: string;
  }[];
}

export type AuthToken = null | string;

export type Locale = 'se' | 'en' | 'no' | 'es' | 'dk' | 'tr';

export type User = getUser_user;

export interface Theme {
  brandColor1: string;
  complimentaryColor1: string;
  brandColor2: string;
  complimentaryColor2: string;
}

export interface Currency {
  symbol: string;
  code: string;
}

declare module '@emotion/react' {
  export interface Theme {
    brandColor1: string;
    complimentaryColor1: string;
    brandColor2: string;
    complimentaryColor2: string;
  }
}

// {new-country-onboarding} Add all new country codes here
export type CountryCode = 'SE' | 'GB' | 'NO' | 'ES' | 'DK' | 'TR' | 'AE';

export type TicketMessage = {
  id: string;
  message: string;
  courseId?: string;
};
